import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { defaultValues } from './columns';

import staticText from 'static/text_display.json';
import { getTimezone } from 'reducers/utils';
import { getSecurityCompanyServices } from 'reducers/securityCompany';
import RootTaskForm from 'containers/Task/Form/RootTask';
import { TASK_TYPE } from 'utils/constants/task';
import { SUB_TASK_COLUMNS } from 'utils/DataTransform/Task/subTask';
import { SCHEDULE_PATTERNS } from 'utils/constants/schedule';
import isEmpty from 'lodash/isEmpty';
import API from 'services/api';
import { cloneTaskInitialValues } from '../../../Task/Form/options';
import { errorHandler } from 'utils/errorMessages';
import { showSnackbar } from 'reducers/ui';
import FormDataPopulation from 'components/Forms/Fields/FormDataPopulation';
import { TASK_FIELDS } from 'utils/formFields/task';
import { validateRequired, validationObject } from 'utils/validations';
import { convertPercentageToimageCordinates } from 'utils/DataTransform/CleanerTask/CleanerTaskDetails';
import {
  SUBTASK_CLEANING_DEFAULT,
  TASK_CLEANING_DEFAULT,
} from 'utils/constants/taskCleaner';
import {
  getGlobalDepartmentRecords,
  getGlobalSiteList,
} from 'selectors/globalSelectors';
import { getGlobalDepartments } from 'utils/personnelHelper';

import { actions as dataGridTableActions } from './Modules/dataGridTable';
import * as dataGridTableSelectors from './Selectors/cleaningTaskSelectors';
import useBusinessClient from 'containers/Pages/People/BusinessClients/Layouts/useBusinessClient';
import useSites from 'containers/Pages/Locations/Sites/Layouts/useSite';
import useClient from 'containers/Pages/People/Customers/Layouts/useClient';
import useAllUsers from 'reducers/customHooks/useAllUsers';

const CleanerForm = ({
  id,
  editMode,
  closeForm,
  updateCleanerTask,
  createCleanerTask,
  cleanerTask,
  showSnackBar,
  timezone,
  globalDepartmentList = [],
  siteList = [],
  persistentStateName,
  size,
  defaultFieldValues, // this props can come from asset detail panel as you can create a task from assets
  disablePresist,
  getAllTemplateTasks, // This will ensure that all the templates are fetched. It is required in assets and sites where all the tmeplates are required
}) => {
  const [resetValues, setResetValues] = useState();
  const bizClients = useBusinessClient();
  const sites = useSites();
  const { rawClients } = useClient();
  const { users } = useAllUsers();
  const onChangeCallback = async ({ values, details }) => {
    try {
      const { data } = isEmpty(details)
        ? await API.fetchTaskDetails({
            id: values.copyFromTemplate.id,
          })
        : details;

      // eslint-disable-next-line prefer-const
      let { tasks, joins, surfaces } = data.data;
      // change the coordinates back to x and y from percentages
      joins = convertPercentageToimageCordinates(joins, surfaces);
      tasks = convertPercentageToimageCordinates(tasks, surfaces);

      // eslint-disable-next-line no-unused-vars
      const { id, ...newFormInitial } = cloneTaskInitialValues({
        taskToCloneId: values.copyFromTemplate.id,
        taskToCloneDetails: { tasks, joins, surfaces },
        defaultValues,
        subTaskDefaultValues: SUBTASK_CLEANING_DEFAULT,
        allowSubTask: true,
        globalDepartmentList,
        siteList,
      });
      // scancode should be null when copying from a template
      const setScanCodeValues = {
        ...newFormInitial,
        scanCode: null,
        scanStartOnly: null,
        taskName: `Copy - ${newFormInitial.taskName}`,
        ...(!isEmpty(defaultFieldValues) && { ...defaultFieldValues }),
      };
      setResetValues(setScanCodeValues);
    } catch (e) {
      //fire notification to show snackbar
      showSnackBar({
        typeOfSnackbar: 'error',
        message: errorHandler(e),
      });
    }
  };
  const dfVals = {
    ...TASK_CLEANING_DEFAULT,
    timezone,
    ...(!isEmpty(defaultFieldValues) && { ...defaultFieldValues }),
    ...(!isEmpty(defaultFieldValues) && {
      disabledFields: Object.keys(defaultFieldValues),
    }),
  };

  const createTask = props => {
    updateCleanerTask(props);
    if (!isEmpty(defaultFieldValues)) {
      // this means this form was opened from some other components detail panel and needs to close
      closeForm();
    }
  };

  const setTaskCategory = !getAllTemplateTasks && {
    taskCategory: [TASK_TYPE.CLEANING.id],
  };
  return (
    <RootTaskForm
      size={size}
      allowSubTask
      id={id}
      editMode={editMode}
      closeForm={closeForm}
      subtaskDefaultValues={SUBTASK_CLEANING_DEFAULT}
      defaultValues={dfVals}
      createTask={createTask}
      editTask={createCleanerTask}
      allowedTaskType={[TASK_TYPE.CLEANING.id]}
      taskDetails={cleanerTask}
      allowToArchive={false}
      sites={sites}
      rawClients={rawClients}
      users={users}
      businessClients={bizClients}
      resetValues={resetValues}
      setResetValues={setResetValues}
      formTitle={
        editMode
          ? staticText.cleaner_operational_view.task.edit_default
          : staticText.cleaner_operational_view.task.create
      }
      subtaskColumns={Object.values(SUB_TASK_COLUMNS)}
      schedulingView={[SCHEDULE_PATTERNS.onceOff.id]}
      {...(!editMode && {
        headerContent: (
          <FormDataPopulation
            formHeader="Select Template to Copy"
            buttonText="Copy Existing Template"
            submitCallback={onChangeCallback}
            subFields={[TASK_FIELDS.template(setTaskCategory)]}
            validationSchema={validationObject({
              copyFromTemplate: validateRequired().nullable(),
            })}
            initialValues={{ copyFromTemplate: null }}
          />
        ),
      })} // generateQR code should not appear here as it is only allowed to be set in tasks panel in managerial view
      omitFields={{
        generalSection: ['newSite'],
        detailedSection: ['generateQRCode'],
      }}
      subtaskOmitFields={{
        generalSection: [],
        detailedSection: ['generateQRCode'],
      }}
      persistentStateName={persistentStateName}
      disablePresist={disablePresist}
    />
  );
};

CleanerForm.propTypes = {
  id: PropTypes.string,
  size: PropTypes.string,
  editMode: PropTypes.bool,
  closeForm: PropTypes.func.isRequired,
  cleanerTask: PropTypes.object,
  createCleanerTask: PropTypes.func,
  updateCleanerTask: PropTypes.func,
  companyId: PropTypes.string,
  persistentStateName: PropTypes.string,
  timezone: PropTypes.string,
  services: PropTypes.array,
  globalDepartmentList: PropTypes.array,
  siteList: PropTypes.array,
  showSnackBar: PropTypes.func,
  defaultFieldValues: PropTypes.object,
  disablePresist: PropTypes.bool,
  getAllTemplateTasks: PropTypes.bool,
};
CleanerForm.defaultProps = {
  getAllTemplateTasks: false,
};
const mapStateToProps = state => ({
  cleanerTask: dataGridTableSelectors.getDetailsTransformedRaw(state),
  services: getSecurityCompanyServices(state),
  timezone: getTimezone(state),
  globalDepartmentList: getGlobalDepartments(getGlobalDepartmentRecords(state)),
  siteList: getGlobalSiteList(state),
});

const mapDispatchToProps = dispatch => ({
  createCleanerTask: ({ data, rootTaskId }) =>
    dispatch(dataGridTableActions.createRowEntry({ data, rootTaskId })),
  updateCleanerTask: ({ id, data }) =>
    dispatch(dataGridTableActions.updateRowEntry({ id, data })),
  showSnackBar: ({ typeOfSnackbar, message }) =>
    dispatch(showSnackbar({ typeOfSnackbar, message })),
});

export default connect(mapStateToProps, mapDispatchToProps)(CleanerForm);
